import { AbacoWebModule, RouterReadyEvent, ApplicationReadyEvent, MenuItem } from "@abaco/web-common/src/app";
import globals from "./globals";
import myRoutes from "./routes";
import DashboardVuexModule from "./state";
import { HTTPClient } from "@abaco/web-common/src/HTTP";
import { createMenu } from "./menu";
import DashboardApi from './api/DashboardApi';
import UserModule from "@abaco/user-module/src/modules/user-module"
import { PinnableTile } from "./models/Dashboard";
import HelperModule from "@abaco/helper-module/src/modules/helper-module";
import SSOModule from "@abaco/sso-web-module/src/abaco-module";
import FarmRegistryModule from "@abaco/farm-registry/src/modules/farm-registry/index";
import GlobalState from "@abaco/portal-global-state/src/modules/global-state/index";
import DemoColdirettiPopUpModule from "@abaco/user-module/src/modules/demoColdirettiPopUp";

export const MODULE_ID = "dashboard_module";

export interface DashboardOption {
    hideUserNavBarItem?: boolean;
    software?: string;
    hasEstraGlobals?: boolean;
    isEmbedded?: boolean;
    hasSecondaryNavBarItem?: boolean;
    hasCompactSidebarMenu?: boolean;
}

const state = new DashboardVuexModule();

export default class DashboardModule implements AbacoWebModule {
    public static MODULE_ID = MODULE_ID;
    public pinnableTiles: PinnableTile[] = [];
    public readonly modulesByTileID: { [key: string]: AbacoWebModule } = {};
    public readonly STANDARD_CONTEXT = "STANDARD";

    private dashApi!: DashboardApi;
    private apiCreated = false;
    private ssoModule!: SSOModule;

    readonly id = MODULE_ID;
    readonly routes = myRoutes;
    readonly vuexModule = state;
    readonly forceTopLevelRoutes = true;
    readonly globalComponents = globals;

    public readonly software!: string;
    public readonly hideUserNavBarItem!: boolean;
    public readonly isEmbedded!: boolean;
    public readonly hasSecondaryNavBarItem!: boolean;
    public readonly hasCompactSidebarMenu!: boolean;
    private readonly hasExtraGlobals!: boolean;

    constructor(options?: DashboardOption) {
        this.software = options?.software || 'portal';
        this.hideUserNavBarItem = options?.hideUserNavBarItem || false;
        this.hasExtraGlobals = options?.hasEstraGlobals || false;
        this.isEmbedded = options?.isEmbedded || false;
        this.hasSecondaryNavBarItem = options?.hasSecondaryNavBarItem || false;
        this.hasCompactSidebarMenu = options?.hasCompactSidebarMenu || false;
    }

    menu: MenuItem[] = [];

    dependsOn = this.software === "coldiretti-demo" ? {
        [FarmRegistryModule.MODULE_ID]: () => new FarmRegistryModule(),
        [UserModule.MODULE_ID]: () => new UserModule(),
        [HelperModule.MODULE_ID]: () => new HelperModule(),
        [GlobalState.MODULE_ID]: () => new GlobalState(),
        [DemoColdirettiPopUpModule.MODULE_ID]: () => new DemoColdirettiPopUpModule()
    } : {
        [FarmRegistryModule.MODULE_ID]: () => new FarmRegistryModule(),
        [UserModule.MODULE_ID]: () => new UserModule(),
        [HelperModule.MODULE_ID]: () => new HelperModule(),
        [GlobalState.MODULE_ID]: () => new GlobalState(),
    }

    onRouterReady(evt: RouterReadyEvent) {
        this.menu = createMenu(evt.router);
    }

    onApplicationReady(evt: ApplicationReadyEvent) {
        let mod: AbacoWebModule | undefined;
        Object.entries(evt.globalComponentsMetadata).forEach(([key, value]) => {
            mod = evt.modules.find(m => m.id === key);
            if (mod) {
                value
                    .filter(v => v.pinnable)
                    .forEach(cmp => {
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        this.modulesByTileID[cmp.id] = mod!;
                        this.pinnableTiles.push({
                            module: key,
                            metadata: cmp,
                            tag: cmp.tag || ''
                        });
                    });
            }
        });
        this.ssoModule = evt.modules.find(m => m.id === SSOModule.MODULE_ID) as SSOModule;
    }

    public getModulesFromTileIDs(tileIDs: string[]): AbacoWebModule[] {
        return tileIDs
            .map((tid) => this.modulesByTileID[tid])
            .filter((val, ind, self) => self.indexOf(val) === ind) // filter unique modules
            .filter((m) => !!m);    // Remove any undefined elements (we should have none but being safe does not hurts)
    }

    async beforeEnterAnyRoutes(http: HTTPClient) {
        if (this.apiCreated) {
            return;
        }
        this.apiCreated = true;
        const DashboardApi = (await import("./api/DashboardApi")).default;
        this.dashApi = new DashboardApi(http, "/dashboard/api/v1", this.software);
    }

    public get dashboardApi() {
        return this.dashApi;
    }

    private get tags() {
        return this.pinnableTiles.map(tile => tile.tag || this.STANDARD_CONTEXT).filter((tag, index, self) => {
            return self.indexOf(tag) === index;
        })
    }

    public async checkAndUpdateCompetence() {
        if (this.vuexModule.viewableTags != null) {
            return;
        }
        const res = await this.ssoModule.competenceResources.checkBulkCompetencesForUser(this.tags.map(t => ({ module: `VIEW_${t}`, software: "DASHBOARD" })))
        this.vuexModule.viewableTags = this.tags.filter(t => res[`DASHBOARD|VIEW_${t}`]);
    }
}
